"use client";

import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import type { BlogPost, BlogPostType } from "@/data/blog";
import { BlogCard } from "@/components/ui/BlogCard";
import { getScrimColor } from "@/utils/colors";
import { renderProse, parseInline } from "@/utils/markdown";
import { LightboxV2, type LightboxItem } from "@/components/ui/LightboxV2";
import { HeartButton } from "@/components/ui/HeartButton";
import { ExternalLinkIcon } from "@/components/ui/Button";

// =============================================================================
// BLOG FEED
// =============================================================================
// Filter chips + masonry grid + expand overlay.
// Masonry uses CSS columns + break-inside-avoid (no library).

const BLOG_VIOLET = "#A78BFA";

function formatDate(iso: string): string {
  const date = new Date(iso + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

type FilterValue = "all" | BlogPostType;

interface FilterChipDef {
  value: FilterValue;
  label: string;
}

const FILTER_CHIPS: FilterChipDef[] = [
  { value: "all", label: "All" },
  { value: "pet", label: "Pets" },
  { value: "post", label: "Posts" },
  { value: "link", label: "Links" },
];

interface BlogFeedProps {
  posts: BlogPost[];
  crtEnabled?: boolean;
  isLightMode?: boolean;
  onLightboxChange?: (isOpen: boolean) => void;
  initialPostId?: string | null;
}

type SortOrder = "newest" | "oldest";

function ShareButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleShare = useCallback(() => {
    const url = `${window.location.origin}/blog/${slug}?ref=share`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [slug]);

  return (
    <button
      onClick={handleShare}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Copy link to share"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono font-bold tracking-widest uppercase"
      style={{
        background: copied ? `${BLOG_VIOLET}22` : hovered ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.07)",
        border: `1px solid ${copied ? BLOG_VIOLET + "66" : hovered ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.12)"}`,
        color: copied ? BLOG_VIOLET : hovered ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.5)",
        transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
      }}
    >
      {copied ? (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          copied
        </>
      ) : (
        <>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          share
        </>
      )}
    </button>
  );
}

// Breakpoints for responsive columns
const LG_BREAKPOINT_PX = 1024;  // 3 columns
const XXL_BREAKPOINT_PX = 1800; // 5 columns for ultra-wide monitors

export function BlogFeed({ posts, crtEnabled = true, isLightMode = false, onLightboxChange, initialPostId }: BlogFeedProps) {
  const scrimColor = getScrimColor(isLightMode);
  const [activeFilter, setActiveFilter] = useState<FilterValue>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Column count is responsive: 2 on mobile, 3 on >=lg. Default 2 so SSR
  // renders the mobile layout; effect below bumps to 3 on wide viewports.
  const [columnCount, setColumnCount] = useState(2);
  const [isStuck, setIsStuck] = useState(false);
  const feedContainerRef = useRef<HTMLDivElement>(null);
  const stickysentinelRef = useRef<HTMLDivElement>(null);

  // Notify parent when lightbox opens/closes
  useEffect(() => {
    onLightboxChange?.(expandedId !== null);
  }, [expandedId, onLightboxChange]);

  // Sync URL when a slugged post is opened or closed
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (expandedId) {
      const post = posts.find(p => p.id === expandedId);
      const slug = post?.type === "post" ? post.slug : undefined;
      if (slug) {
        window.history.pushState({}, "", `/blog/${slug}`);
        return;
      }
    }
    // Lightbox closed or non-slugged post — return to /blog
    if (window.location.pathname !== "/blog") {
      window.history.pushState({}, "", "/blog");
    }
  }, [expandedId, posts]);

  // Open a post when a deep link slug is passed in. The ref guard ensures we
  // open the post once per distinct initialPostId — otherwise the effect re-fires
  // on every parent re-render (because `posts` is a fresh array each render),
  // which reopens the lightbox immediately after the user closes it.
  const lastHandledInitialPostIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialPostId) return;
    if (lastHandledInitialPostIdRef.current === initialPostId) return;
    const post = posts.find(p => p.id === initialPostId);
    if (post) {
      lastHandledInitialPostIdRef.current = initialPostId;
      setExpandedId(post.id);
    }
  }, [initialPostId, posts]);

  // Detect when sticky bar becomes stuck using IntersectionObserver on sentinel
  useEffect(() => {
    const sentinel = stickysentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel scrolls out of view (above viewport), chips are stuck
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Responsive column count -- matchMedia is the cheap, no-reflow way to
  // react to viewport changes without sprinkling resize listeners.
  useEffect(() => {
    const mqLg = window.matchMedia(`(min-width: ${LG_BREAKPOINT_PX}px)`);
    const mqXxl = window.matchMedia(`(min-width: ${XXL_BREAKPOINT_PX}px)`);
    const update = () => {
      if (mqXxl.matches) setColumnCount(5);
      else if (mqLg.matches) setColumnCount(3);
      else setColumnCount(2);
    };
    update();
    mqLg.addEventListener("change", update);
    mqXxl.addEventListener("change", update);
    return () => {
      mqLg.removeEventListener("change", update);
      mqXxl.removeEventListener("change", update);
    };
  }, []);

  // Sort posts by date in the current direction, then filter
  const sortedPosts = useMemo(() => {
    const dir = sortOrder === "newest" ? -1 : 1;
    return [...posts].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -dir : dir;
      // Same-day entries: stable tiebreak by id so order never shuffles.
      return a.id < b.id ? -dir : a.id > b.id ? dir : 0;
    });
  }, [posts, sortOrder]);

  // Feed cards exclude unlisted posts. Lightbox lookup keeps the full posts prop,
  // so a deep link to an unlisted post still resolves — see lightboxItems below.
  const listedPosts = useMemo(() => sortedPosts.filter((p) => !p.unlisted), [sortedPosts]);

  const visiblePosts = useMemo(() => {
    if (activeFilter === "all") return listedPosts;
    return listedPosts.filter((p) => p.type === activeFilter);
  }, [listedPosts, activeFilter]);

  // Row-major column distribution.
  //
  // CSS `columns` would read TOP->BOTTOM *each* column, then continue to the
  // next column (newspaper order). For a chronological feed that reads wrong:
  // the second-newest entry ends up below the newest in the SAME column, not
  // to its right. We want Z-order:
  //
  //   1  2  3
  //   4  5  6
  //   7  8  9
  //
  // So we split into N column arrays, placing item `i` into column `i % N`.
  // Then render the columns as flex-col siblings in a flex-row parent. Each
  // column grows independently -- heights pack naturally within each column
  // the way masonry should.
  const columns = useMemo(() => {
    const cols: BlogPost[][] = Array.from({ length: columnCount }, () => []);
    visiblePosts.forEach((post, i) => {
      cols[i % columnCount].push(post);
    });
    return cols;
  }, [visiblePosts, columnCount]);

  // Counts per type (for filter chip labels) — excludes unlisted posts
  const counts = useMemo(() => {
    const c: Record<FilterValue, number> = { all: listedPosts.length, post: 0, pet: 0, link: 0 };
    for (const p of listedPosts) c[p.type]++;
    return c;
  }, [listedPosts]);

  // If a deep link opens an unlisted post (in `posts` but excluded from feed/visiblePosts),
  // show it as a single-item lightbox so prev/next doesn't leak to other content.
  const expandedUnlistedPost = useMemo(() => {
    if (!expandedId) return null;
    const post = posts.find((p) => p.id === expandedId);
    return post && post.unlisted ? post : null;
  }, [expandedId, posts]);

  // The post list the lightbox actually iterates. Either the feed (visiblePosts)
  // or a single unlisted post when deep-linked.
  const lightboxSource = useMemo(
    () => (expandedUnlistedPost ? [expandedUnlistedPost] : visiblePosts),
    [expandedUnlistedPost, visiblePosts]
  );

  // Lightbox navigation: find the expanded post within the active source list.
  const expandedIndex = useMemo(() => {
    if (!expandedId) return -1;
    return lightboxSource.findIndex((p) => p.id === expandedId);
  }, [expandedId, lightboxSource]);

  // Convert source posts into LightboxV2 items.
  // Pet videos get audio=true (gig-style). Prose posts use the "prose" type.
  const lightboxItems = useMemo((): LightboxItem[] =>
    lightboxSource.map((post) => {
      const meta = (
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-bold tracking-widest uppercase"
            style={{
              background: `${BLOG_VIOLET}22`,
              color: BLOG_VIOLET,
              border: `1px solid ${BLOG_VIOLET}44`,
            }}
          >
            {post.type}
          </span>
          <span className="text-[rgba(255,255,255,0.4)] text-[10px] font-mono uppercase tracking-widest">
            {formatDate(post.date)}
          </span>
        </div>
      );

      if (post.type === "post") {
        return {
          id: post.id,
          type: "prose" as const,
          title: post.title ? (
            <>
              {post.title}
              {post.subtitle && (
                <span
                  className="block text-sm font-normal font-mono mt-1"
                  style={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.01em" }}
                >
                  {post.subtitle}
                </span>
              )}
            </>
          ) : undefined,
          proseContent: (
            <>
              {renderProse(post.body, post.id, { dropCap: true })}
              {/* Spacer below the heart pushes it above the scroll container's
                  bottom gradient mask so a reader scrolled to the end sees a
                  fully opaque heart. */}
              <div className="mt-8 mb-16 flex justify-center items-center gap-3">
                <HeartButton postId={post.id} />
                {post.slug && <ShareButton slug={post.slug} />}
              </div>
            </>
          ),
          meta,
        };
      }

      // link — preview + note + a Visit button. Reuses the "prose" lightbox
      // slot so LightboxV2 needs no new item type. Reachable via prev/next;
      // clicking a link card in the feed opens the URL directly.
      if (post.type === "link") {
        const host = (() => {
          try { return new URL(post.url).hostname.replace(/^www\./, ""); } catch { return undefined; }
        })();
        return {
          id: post.id,
          type: "prose" as const,
          title: post.title,
          proseContent: (
            <div className="flex flex-col items-center text-center">
              {post.image && (
                <img
                  src={post.image}
                  alt=""
                  className="w-full max-w-md rounded-xl mb-8 aspect-[16/9] object-cover"
                />
              )}
              {post.note && <p className="max-w-xl">{parseInline(post.note, post.id)}</p>}
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-mono font-bold tracking-widest uppercase text-[#A78BFA] bg-[#A78BFA]/15 hover:bg-[#A78BFA]/30 border border-[#A78BFA]/40 transition-colors"
              >
                Visit{host ? ` ${host}` : ""}
                <ExternalLinkIcon size={14} />
              </a>
            </div>
          ),
          meta,
        };
      }

      // pet — video takes priority over image
      if (post.video) {
        return {
          id: post.id,
          type: "video" as const,
          src: post.video,
          poster: post.image,
          alt: post.alt,
          caption: post.caption ? parseInline(post.caption, post.id) : undefined,
          audio: true, // blog videos are gig-style — audio on
          meta,
        };
      }

      return {
        id: post.id,
        type: "image" as const,
        src: post.image,
        alt: post.alt,
        caption: post.caption ? parseInline(post.caption, post.id) : undefined,
        meta,
      };
    }),
    [lightboxSource]
  );

  const handleCloseExpand = useCallback(() => {
    setExpandedId(null);
  }, []);

  const handleNavigate = useCallback((index: number) => {
    setExpandedId(lightboxSource[index].id);
  }, [lightboxSource]);

  const handleFilterChange = useCallback((value: FilterValue) => {
    setActiveFilter(value);
    // Scroll the parent BlogOverlay container to the top of the feed
    if (feedContainerRef.current) {
      feedContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleSortToggle = useCallback(() => {
    setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"));
    // Bring the user back to the top -- flipping the order otherwise leaves them
    // stranded mid-feed looking at entries that aren't adjacent to anything.
    if (feedContainerRef.current) {
      feedContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <div ref={feedContainerRef} className="pb-32">
      {/* Sentinel for sticky detection — when this scrolls out, chips are stuck */}
      <div ref={stickysentinelRef} className="h-0" aria-hidden="true" />

      {/* Small visual gap before chips land in their natural position. The
          sticky `top` below keeps the bar under the hero name when stuck, so we
          don't need the big responsive spacer that used to live here. */}
      <div className="h-2 sm:h-4" />

      {/* Sticky filter chips bar -- persistent nav while scrolling the feed.
          When stuck, the bar snaps into the slot where the hero subtitles
          were sitting — below the (faded) name, no overlap. The ghost-name
          stays as a top-of-frame anchor while content takes focus.

          Important: `top` here is relative to the scroll parent (BlogOverlay's
          container), which is already offset from the viewport by z1Height
          (~56px). So viewport-Y of the pill bar = z1Height + this `top` value
          + chip row's internal py. To land at subtitle viewport-Y:
            Desktop (subtitle ≈ y=100): 100 - 56 - py(20) ≈  24 → top-6
            Mobile  (subtitle ≈ y= 80):  80 - 56 - py(8)  ≈  16 → top-4
            Narrow  (subtitle ≈ y= 55):  55 - 56         ≈   0 → top-2 (min)
          clamp(8px, 2vw, 28px) tracks this. */}
      {/* Scrim matches the manifesto top scrim: gradient fades out below chips,
          no hard border, content scrolls underneath and darkens naturally. */}
      <div
        className="sticky z-20 mb-4"
        style={{ top: "clamp(8px, 2vw, 28px)" }}
      >
        {/* Gradient scrim behind the chips -- fades in when chips become stuck */}
        <div
          className="absolute inset-x-0 -top-20 lg:-top-28 xl:-top-36 pointer-events-none transition-opacity duration-300"
          style={{
            height: "calc(220% + 144px)",
            background: `linear-gradient(to bottom, rgba(${scrimColor},0.92) 0%, rgba(${scrimColor},0.92) 20%, rgba(${scrimColor},0.75) 45%, rgba(${scrimColor},0.3) 75%, transparent 100%)`,
            opacity: isStuck ? 1 : 0,
          }}
        />
        {/* Chip row -- sits above the scrim.
            overflow-x-auto + whitespace-nowrap lets the row scroll horizontally
            once the chip count grows (future: more filter types, view toggle,
            date picker). `scrollbar-none` (global util) hides the scrollbar so
            the scroll is felt, not seen. */}
        <div className="relative max-w-5xl 2xl:max-w-[1600px] mx-auto flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-6 py-2 sm:py-5 overflow-x-auto scrollbar-none">
          {/* Sort toggle -- on left. Hidden when viewing an empty filter (e.g. Posts with 0 entries). */}
          {!(activeFilter !== "all" && counts[activeFilter] === 0) && (
            <button
              type="button"
              onClick={handleSortToggle}
              className="px-2.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-mono font-bold tracking-wider sm:tracking-widest uppercase transition-all select-none inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap"
              style={{
                background: "rgba(var(--foreground-rgb), 0.08)",
                color: "rgba(var(--foreground-rgb), 0.72)",
                border: "1px solid rgba(var(--foreground-rgb), 0.22)",
                textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
              }}
              aria-label={
                sortOrder === "newest"
                  ? "Sorted newest first. Click to sort oldest first."
                  : "Sorted oldest first. Click to sort newest first."
              }
            >
              {sortOrder === "newest" ? "Newest" : "Oldest"}
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{
                  transform: sortOrder === "oldest" ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 300ms ease",
                }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          )}

          {FILTER_CHIPS.map((chip) => {
            const isActive = activeFilter === chip.value;
            const count = counts[chip.value];
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => handleFilterChange(chip.value)}
                className="px-2.5 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-mono font-bold tracking-wider sm:tracking-widest uppercase transition-all select-none whitespace-nowrap"
                style={{
                  background: isActive ? `${BLOG_VIOLET}33` : "rgba(var(--foreground-rgb), 0.08)",
                  color: isActive ? BLOG_VIOLET : "rgba(var(--foreground-rgb), 0.72)",
                  border: `1px solid ${isActive ? `${BLOG_VIOLET}66` : "rgba(var(--foreground-rgb), 0.22)"}`,
                  textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
                }}
              >
                {chip.label} <span className="opacity-60 ml-1">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Masonry grid -- flex row of N columns, each a flex col. Row-major
          distribution gives Z-order reading (1,2,3 across the top row, 4,5,6
          next, etc.) which CSS `columns` cannot do. Column heights pack
          naturally because each column is its own flex-col. */}
      <div className="max-w-5xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-6">
        {visiblePosts.length > 0 ? (
          <div className="flex items-start gap-2 sm:gap-4">
            {columns.map((col, colIdx) => (
              <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-2 sm:gap-4">
                {col.map((post) => (
                  <BlogCard
                    key={post.id}
                    post={post}
                    mode="compact"
                    crtEnabled={crtEnabled}
                    onClick={() => setExpandedId(post.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p
              className="text-sm font-mono uppercase tracking-widest"
              style={{ color: activeFilter === "post" ? BLOG_VIOLET : "rgba(var(--foreground-rgb), 0.4)" }}
            >
              {activeFilter === "post" ? (<>Writing in progress.<br />Come back soon.</>) : "Nothing here yet"}
            </p>
          </div>
        )}
      </div>

      {/* Lightbox — portal to document.body so it escapes BlogOverlay's mask-image */}
      {expandedIndex !== -1 && (
        <LightboxV2
          items={lightboxItems}
          currentIndex={expandedIndex}
          onClose={handleCloseExpand}
          onNavigate={handleNavigate}
          wrapNavigation={true}
        />
      )}
    </div>
  );
}
